import axios from "axios";

const API_BASE = process.env.REACT_APP_API_BASE?.trim() || ""; // e.g. https://lhpjjzns6f.execute-api.ap-southeast-2.amazonaws.com

export interface ProductRow {
	productId: string;
	name: string;
	price?: number;
	promoText?: string | null;
	shopType?: number | string | null;
	sizeValue?: number | null;
	sizeUnit?: string | null;
	imageUrl?: string | null;
	// Combined size string for display, e.g. "500 g" or "2 L"
	size?: string | null;
}

export interface FetchProductsParams {
	page: number; // 1-based
	pageSize: number;
	name?: string;
	shopType?: number;
}

export interface FetchProductsResult {
	total: number;
	items: ProductRow[];
}

export async function fetchProducts(
	params: FetchProductsParams,
): Promise<FetchProductsResult> {
	const url = `${API_BASE}/api/Products`;
	const res = await axios.get(url, { params });
	const data = res.data ?? {};

	// Support both camelCase and PascalCase payloads
	const rawItems: ProductRow[] = data.products ?? data.Products ?? [];
	// Build combined size field on the client for compatibility with different backends
	const items: ProductRow[] = rawItems.map((it) => {
		const imageUrl =
			(it as any).imageUrl ?? (it as any).ImageUrl ?? it.imageUrl ?? null;
		const promoText =
			(it as any).promoText ?? (it as any).PromoText ?? it.promoText ?? null;
		const sizeExisting = (it as any).size ?? (it as any).Size;
		if (sizeExisting) return { ...it, imageUrl, size: sizeExisting, promoText };

		const v = it.sizeValue;
		const u = it.sizeUnit;
		let size: string | null = null;
		if (v != null && u != null && u !== "") {
			// keep decimal as-is
			size = `${v} ${u}`;
		} else if (v != null) {
			size = String(v);
		} else if (u != null) {
			size = u;
		}
		return { ...it, imageUrl, size, promoText };
	});
	const total: number = data.count ?? data.Count ?? 0;

	return { total, items };
}

// ── Data freshness (BTS-160 / BTS-161) ──────────────────────────────────────

export interface LastUpdated {
	/** Latest refresh across all shops (ISO, UTC), or null when there is no data. */
	lastUpdatedAt: string | null;
	/** Latest refresh per shop type ("0" = Coles, "1" = Woolworths). */
	shops: Record<string, string | null>;
}

export async function fetchLastUpdated(): Promise<LastUpdated> {
	const res = await axios.get(`${API_BASE}/api/Products/last-updated`);
	const data = res.data ?? {};
	const shops: Record<string, string | null> = {};
	for (const s of data.shops ?? data.Shops ?? []) {
		const type = s.shopType ?? s.ShopType;
		if (type != null) shops[String(type)] = s.lastUpdatedAt ?? s.LastUpdatedAt ?? null;
	}
	return { lastUpdatedAt: data.lastUpdatedAt ?? data.LastUpdatedAt ?? null, shops };
}

/** Days after which the data counts as stale (the freshness chip turns amber). */
export const STALE_AFTER_DAYS = 7;

/**
 * "today", "yesterday" or "21 Oct" in the viewer's time zone, so a stale import
 * never reads as fresh. Returns null for a missing or unparsable date.
 */
export function formatUpdatedDate(iso?: string | null, now: Date = new Date()): string | null {
	if (!iso) return null;
	const d = new Date(iso);
	if (Number.isNaN(d.getTime())) return null;
	const startOf = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
	const days = Math.round((startOf(now) - startOf(d)) / 86_400_000);
	if (days === 0) return "today";
	if (days === 1) return "yesterday";
	return d.toLocaleDateString("en-AU", {
		day: "numeric",
		month: "short",
		...(d.getFullYear() !== now.getFullYear() ? { year: "numeric" } : {}),
	});
}

export function isStale(iso?: string | null, now: Date = new Date()): boolean {
	if (!iso) return true;
	const t = new Date(iso).getTime();
	return Number.isNaN(t) || now.getTime() - t > STALE_AFTER_DAYS * 86_400_000;
}

// ── AI search types ──────────────────────────────────────────────────────────

export interface ProductSearchItem {
	productId: string;
	name: string;
	shopType?: number | null;
	brand?: string | null;
	sizeValue?: number | null;
	sizeUnit?: string | null;
	imageUrl?: string | null;
	price?: number | null;
	promoText?: string | null;
}

export interface DescriptionSearchResponse {
	query: string;
	inferredProducts: string[];
	remainingSearches: number;
	products: ProductSearchItem[];
}

// ── AI search function ────────────────────────────────────────────────────────

export async function searchByDescription(
	query: string,
	topN: number = 10,
	shopType?: number,
): Promise<DescriptionSearchResponse> {
	const url = `${API_BASE}/api/Products/search-by-description`;
	const response = await fetch(url, {
		method: "POST",
		headers: { "Content-Type": "application/json" },
		body: JSON.stringify({ query, topN, shopType: shopType ?? null }),
	});

	if (response.status === 429) {
		const errorBody = await response.json().catch(() => ({}));
		const err = new Error(
			(errorBody as any).message ?? "Daily AI search limit reached. Try again tomorrow.",
		) as Error & { status: number };
		err.status = 429;
		throw err;
	}

	if (!response.ok) {
		throw new Error(`Search failed with status ${response.status}`);
	}

	return response.json() as Promise<DescriptionSearchResponse>;
}
